// AT-IA-3.1 (2026-06-06) — Refator: 3 fontes separadas (contrato + ART + relatorio).
// Cada fonte vai pra uma chamada IA dedicada com prompt focado. Auto-detect
// pelo nome do arquivo (contem "contrato"/"ART"/"relatorio").

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Modal, Button, Space, Typography, Alert, Checkbox, Tag, Select,
  Spin, message, App as AntApp, Tabs, Tooltip,
} from 'antd';
import {
  RobotOutlined, FilePdfOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Text } = Typography;

interface Props {
  open: boolean;
  atestadoId: string;
  contratoId: string;
  atestadoAtual: any;
  onCancel: () => void;
  onAplicar: (campos: { [k: string]: any }) => void;
}

interface DocDrive {
  id: string;       // UUID em operacao.documentos
  nome: string;
  s3_key: string;
  pasta: string | null;
}

// Auto-detect: tenta classificar PDF como contrato/art/relatorio pelo nome
function classificarPorNome(nome: string): 'contrato' | 'art' | 'relatorio' | null {
  const n = nome.toLowerCase();
  if (/\bart[ \-_]|\bart\b|crea[\-_]rs|registro[\- ]?profission/i.test(n)) return 'art';
  if (/contrato|contract|minuta/i.test(n)) return 'contrato';
  if (/relat[oó]rio|\bRT[\-_]|\brt\b\.pdf|final\.pdf|parecer/i.test(n)) return 'relatorio';
  return null;
}

function ModalSugerirAtestadoInner({
  open, atestadoId, contratoId, atestadoAtual, onCancel, onAplicar,
}: Props) {
  const [docs, setDocs] = useState<DocDrive[]>([]);
  const [carregandoDocs, setCarregandoDocs] = useState(false);
  const [docContrato, setDocContrato] = useState<string | null>(null);
  const [docArt, setDocArt] = useState<string | null>(null);
  const [docRelatorio, setDocRelatorio] = useState<string | null>(null);
  const [processando, setProcessando] = useState(false);
  const [resultado, setResultado] = useState<any | null>(null);
  const [aplicar, setAplicar] = useState<Record<string, boolean>>({});

  // Auto-classifica e pre-seleciona
  useEffect(() => {
    if (!open || !contratoId) return;
    (async () => {
      setCarregandoDocs(true);
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_drive_listar_arvore', contrato_id: contratoId },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const pdfs: DocDrive[] = (d.files || []).filter((f: any) => /\.pdf$/i.test(f.s3_key || ''));
        setDocs(pdfs);

        // Auto-preenche os 3 dropdowns
        let achouContrato: string | null = null;
        let achouArt: string | null = null;
        let achouRelatorio: string | null = null;
        for (const p of pdfs) {
          const tipo = classificarPorNome(p.nome);
          if (tipo === 'contrato' && !achouContrato) achouContrato = p.id;
          else if (tipo === 'art' && !achouArt) achouArt = p.id;
          else if (tipo === 'relatorio' && !achouRelatorio) achouRelatorio = p.id;
        }
        setDocContrato(achouContrato);
        setDocArt(achouArt);
        setDocRelatorio(achouRelatorio);
      } catch (e: any) {
        message.error('Falha ao carregar docs: ' + (e.response?.data?.error || e.message));
      } finally { setCarregandoDocs(false); }
    })();
  }, [open, contratoId]);

  const totalEscolhido = [docContrato, docArt, docRelatorio].filter(Boolean).length;

  async function processar() {
    if (totalEscolhido === 0) {
      message.warning('Escolha ao menos 1 documento');
      return;
    }
    setProcessando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        {
          action: 'op_atestado_sugerir_preenchimento',
          atestado_id: atestadoId,
          doc_contrato_id: docContrato,
          doc_art_id: docArt,
          doc_relatorio_id: docRelatorio,
        },
        { headers: { Authorization: token }, timeout: 120000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      // Marca todos os campos sugeridos como "aplicar" por default
      const map: Record<string, boolean> = {};
      for (const k of Object.keys(d.sugestao || {})) map[k] = true;
      setAplicar(map);
      message.success('IA gerou sugestoes');
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setProcessando(false); }
  }

  function aplicarSelecao() {
    if (!resultado?.sugestao) return;
    const out: Record<string, any> = {};
    for (const k of Object.keys(aplicar)) {
      if (aplicar[k]) out[k] = resultado.sugestao[k];
    }
    onAplicar(out);
    reset();
  }

  function reset() {
    setResultado(null);
    setAplicar({});
  }

  const optsDoc = useMemo(() => docs.map(d => ({
    value: d.id,
    label: d.nome,
  })), [docs]);

  const sug = resultado?.sugestao || {};
  const fontes = resultado?.fontes || {};
  const camposExtraidos = Object.keys(sug);

  // Rotulo amigavel
  const LABELS: Record<string, string> = {
    cliente_razao_social: 'Cliente — Razão social',
    cliente_cnpj: 'Cliente — CNPJ',
    cliente_endereco: 'Cliente — Endereço',
    representante_legal_nome: 'Representante legal — Nome',
    representante_legal_cpf: 'Representante legal — CPF',
    objeto_descricao: 'Objeto descrição',
    equipe_tecnica: 'Equipe técnica',
    numero_art_referencia: 'Número da ART (referência)',
    inicio_servicos_art: 'Início dos serviços (ART)',
    fim_servicos_art: 'Fim dos serviços (ART)',
    escopo_descritivo: 'Escopo descritivo',
    atividades_agrupadas: 'Atividades agrupadas',
    equipamentos_softwares: 'Equipamentos / softwares',
  };

  // Fonte de cada campo (cor da tag)
  const FONTE_DO_CAMPO: Record<string, 'contrato' | 'art' | 'relatorio'> = {
    cliente_razao_social: 'contrato',
    cliente_cnpj: 'contrato',
    cliente_endereco: 'contrato',
    representante_legal_nome: 'contrato',
    representante_legal_cpf: 'contrato',
    objeto_descricao: 'contrato',
    equipe_tecnica: 'art',
    numero_art_referencia: 'art',
    inicio_servicos_art: 'art',
    fim_servicos_art: 'art',
    escopo_descritivo: 'relatorio',
    atividades_agrupadas: 'relatorio',
    equipamentos_softwares: 'relatorio',
  };
  const CORES_FONTE = {
    contrato: 'blue',
    art: 'orange',
    relatorio: 'purple',
  };

  return (
    <Modal open={open} onCancel={() => { reset(); onCancel(); }}
      title={<Space><RobotOutlined /> Preencher atestado com IA (3 fontes)</Space>}
      width={resultado ? 1100 : 720}
      footer={
        resultado
          ? [
              <Button key="c" onClick={() => { reset(); onCancel(); }}>Cancelar</Button>,
              <Button key="r" onClick={reset}>Recomeçar</Button>,
              <Button key="a" type="primary" onClick={aplicarSelecao}
                disabled={Object.values(aplicar).every(v => !v)}>
                Aplicar selecionados ({Object.values(aplicar).filter(Boolean).length})
              </Button>,
            ]
          : [
              <Button key="c" onClick={onCancel}>Cancelar</Button>,
              <Button key="p" type="primary" loading={processando} onClick={processar}
                disabled={totalEscolhido === 0}>
                Processar {totalEscolhido > 0 ? `(${totalEscolhido} fonte${totalEscolhido > 1 ? 's' : ''})` : ''}
              </Button>,
            ]
      }
      destroyOnClose>

      {!resultado && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert type="info" showIcon
            message="Escolha os 3 documentos do Drive do contrato"
            description="Cada documento alimenta uma chamada IA específica com prompt focado. Auto-detectei pelo nome do arquivo — confira/ajuste antes de processar." />

          {carregandoDocs ? <Spin /> : (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <div>
                <Text strong style={{ fontSize: 12, color: '#0930D0' }}>
                  <FilePdfOutlined /> Contrato
                </Text>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                  → cliente, CNPJ, endereço, representante legal, objeto
                </Text>
                <Select allowClear showSearch placeholder="(sem contrato)" value={docContrato ?? undefined}
                  onChange={(v) => setDocContrato(v ?? null)} options={optsDoc}
                  style={{ width: '100%' }} optionFilterProp="label" />
              </div>
              <div>
                <Text strong style={{ fontSize: 12, color: '#D46B08' }}>
                  <FilePdfOutlined /> ART (CREA)
                </Text>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                  → equipe técnica, RT, registros, períodos
                </Text>
                <Select allowClear showSearch placeholder="(sem ART)" value={docArt ?? undefined}
                  onChange={(v) => setDocArt(v ?? null)} options={optsDoc}
                  style={{ width: '100%' }} optionFilterProp="label" />
              </div>
              <div>
                <Text strong style={{ fontSize: 12, color: '#722ED1' }}>
                  <FileTextOutlined /> Relatório técnico
                </Text>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                  → escopo descritivo, atividades agrupadas, equipamentos/softwares (Sonnet)
                </Text>
                <Select allowClear showSearch placeholder="(sem relatório)" value={docRelatorio ?? undefined}
                  onChange={(v) => setDocRelatorio(v ?? null)} options={optsDoc}
                  style={{ width: '100%' }} optionFilterProp="label" />
              </div>
            </Space>
          )}

          <Alert type="warning" showIcon
            message={totalEscolhido === 0
              ? 'Selecione ao menos 1 fonte'
              : `${totalEscolhido} fonte${totalEscolhido > 1 ? 's' : ''} selecionada${totalEscolhido > 1 ? 's' : ''} — custo ~$${(0.05 * totalEscolhido + (docRelatorio ? 0.15 : 0)).toFixed(2)}`} />

          {processando && (
            <Space direction="vertical" align="center" style={{ width: '100%', padding: 20 }}>
              <Spin size="large" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {totalEscolhido} chamada{totalEscolhido > 1 ? 's' : ''} IA em paralelo... (~20-40s)
              </Text>
            </Space>
          )}
        </Space>
      )}

      {resultado && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert type="success" showIcon
            message="Sugestões da IA"
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space size={12} wrap>
                  {fontes.contrato?.doc_nome && (
                    <Tag color="blue">Contrato: {fontes.contrato.campos_extraidos?.length || 0} campos</Tag>
                  )}
                  {fontes.art?.doc_nome && (
                    <Tag color="orange">ART: {fontes.art.campos_extraidos?.length || 0} campos</Tag>
                  )}
                  {fontes.relatorio?.doc_nome && (
                    <Tag color="purple">Relatório: {fontes.relatorio.campos_extraidos?.length || 0} campos</Tag>
                  )}
                  <Tag>Tokens: {resultado.meta?.tokens_in}/{resultado.meta?.tokens_out}</Tag>
                </Space>
              </Space>
            } />

          {/* Alerta quando alguma fonte teve 0 chars extraídos */}
          {(['contrato','art','relatorio'] as const).filter(f =>
            fontes[f]?.doc_nome && (fontes[f]?.chars || 0) === 0
          ).map(f => (
            <Alert key={f} type="warning" showIcon style={{ marginTop: 4 }}
              message={`Fonte "${f === 'contrato' ? 'Contrato' : f === 'art' ? 'ART' : 'Relatório'}" sem texto extraído`}
              description={
                <span>
                  O PDF "<strong>{fontes[f].doc_nome}</strong>" não tem texto extraível
                  (pode ser PDF escaneado/imagem ou ainda não foi processado).
                  Tente outro PDF ou aguarde alguns segundos e tente de novo.
                </span>
              } />
          ))}

          <Space style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => {
              const m: Record<string, boolean> = {};
              for (const k of camposExtraidos) m[k] = true;
              setAplicar(m);
            }}>Marcar todos</Button>
            <Button size="small" onClick={() => setAplicar({})}>Desmarcar todos</Button>
          </Space>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ width: 30, padding: 6 }}></th>
                <th style={{ textAlign: 'left', padding: 6, width: 200 }}>Campo</th>
                <th style={{ textAlign: 'left', padding: 6, width: 70 }}>Fonte</th>
                <th style={{ textAlign: 'left', padding: 6, width: 200 }}>Atual</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Sugerido (IA)</th>
              </tr>
            </thead>
            <tbody>
              {camposExtraidos.map(k => {
                const fonte = FONTE_DO_CAMPO[k];
                const atual = (atestadoAtual || {})[k];
                const sugerido = sug[k];
                return (
                  <tr key={k} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 6 }}>
                      <Checkbox checked={!!aplicar[k]}
                        onChange={e => setAplicar({ ...aplicar, [k]: e.target.checked })} />
                    </td>
                    <td style={{ padding: 6, fontWeight: 600 }}>{LABELS[k] || k}</td>
                    <td style={{ padding: 6 }}>
                      {fonte && <Tag color={CORES_FONTE[fonte]} style={{ fontSize: 9 }}>{fonte}</Tag>}
                    </td>
                    <td style={{ padding: 6, color: '#888', fontSize: 11 }}>
                      {atual == null || atual === ''
                        ? <Text type="secondary" italic>(vazio)</Text>
                        : <PreviewValor v={atual} />}
                    </td>
                    <td style={{ padding: 6, color: '#020D7F', fontSize: 11 }}>
                      <PreviewValor v={sugerido} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Space>
      )}
    </Modal>
  );
}

function PreviewValor({ v }: { v: any }) {
  if (v == null || v === '') return <Text type="secondary" italic>(vazio)</Text>;
  if (Array.isArray(v)) {
    return <Text style={{ fontSize: 10 }}>{v.length} item(s) • {JSON.stringify(v).slice(0, 100)}...</Text>;
  }
  if (typeof v === 'object') return <Text style={{ fontSize: 10 }}>{JSON.stringify(v).slice(0, 120)}</Text>;
  const s = String(v);
  return <Text>{s.length > 200 ? s.slice(0, 200) + '...' : s}</Text>;
}

export default function ModalSugerirAtestado(props: Props) {
  return <AntApp><ModalSugerirAtestadoInner {...props} /></AntApp>;
}
